import type { Attendance, DemoData, Member, MemberStatus, Payment } from './demoTypes';

export function getLocalDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getLocalDateTime(date = new Date()): string {
  return `${getLocalDateKey(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
}

export function shiftDateKey(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T12:00:00`);
  date.setDate(date.getDate() + days);
  return getLocalDateKey(date);
}

export const DEMO_TODAY = getLocalDateKey();
export const DEMO_MODE = process.env.NEXT_PUBLIC_DEMO_MODE !== 'false';

export const demoCredentials = {
  student: { dni: '38291047', label: 'Lucía Fernández' },
  professor: { email: 'prof.martinez@gymos.ar', password: 'Profe2026!', label: 'Prof. Martínez' },
  admin: { email: 'admin@gymos.ar', password: 'Admin2026!', label: 'Administrador' },
} as const;

const memberNames = [
  'Lucía Fernández', 'Gonzalo Pereyra', 'Valentina Sosa', 'Matías Romero', 'Camila Núñez',
  'Sebastián Díaz', 'Florencia López', 'Agustín Torres', 'Micaela Herrera', 'Ramiro Gutiérrez',
  'Natalia Vega', 'Diego Molina', 'Sofía Acosta', 'Tomás Benítez', 'Julieta Castro',
  'Nicolás Domínguez', 'Martina Escobar', 'Franco Ferraro', 'Paula Giménez', 'Bruno Ibarra',
  'Carolina Juárez', 'Emiliano Klein', 'Abril Luna', 'Federico Méndez', 'Rocío Navarro',
  'Ignacio Ortiz', 'Malena Paz', 'Joaquín Quiroga', 'Clara Rivas', 'Lautaro Suárez',
];

const knownDnis = ['38291047', '35128743', '40183926', '33748291', '41029384'];
const morosoIndexes = new Set([1, 3, 7, 11, 18]);
const suspendedIndexes = new Set([5, 24]);
const inactiveIndexes = new Set([9, 27, 29]);

function statusFor(index: number): MemberStatus {
  if (morosoIndexes.has(index)) return 'Moroso';
  if (suspendedIndexes.has(index)) return 'Suspendido';
  if (inactiveIndexes.has(index)) return 'Inactivo';
  return 'Activo';
}

const members: Member[] = memberNames.map((name, index) => {
  const status = statusFor(index);
  const branchId = index % 2 === 0 ? 'palermo' : 'belgrano';
  const daysOverdue = status === 'Moroso' || status === 'Suspendido' ? 8 + ((index * 7) % 39) : 0;
  return {
    id: `member-${String(index + 1).padStart(3, '0')}`,
    name,
    dni: knownDnis[index] ?? String(30000000 + index * 317429),
    branchId,
    planId: index % 5 === 0 ? 'plan-musculacion' : index % 3 === 0 ? 'plan-3-dias' : 'plan-completo',
    status,
    joinedAt: index < 4 ? `2026-08-${String(3 + index * 5).padStart(2, '0')}` : `2026-${index % 4 < 2 ? '07' : '0' + ((index % 5) + 1)}-${String((index % 20) + 1).padStart(2, '0')}`,
    nextDue: status === 'Inactivo' ? null : daysOverdue ? `2026-08-${String(Math.max(1, 27 - daysOverdue)).padStart(2, '0')}` : `2026-09-${String((index % 15) + 1).padStart(2, '0')}`,
    daysOverdue,
    professorId: branchId === 'palermo' ? (index % 4 === 0 ? 'prof-003' : 'prof-001') : 'prof-002',
    routineId: `routine-${String((index % 3) + 1).padStart(3, '0')}`,
    email: `${name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '.')}@demo.gymos.ar`,
    phone: `11 4${String(1000000 + index * 17391).slice(0, 7)}`,
    birthDate: `${1984 + (index % 18)}-${String((index % 12) + 1).padStart(2, '0')}-${String((index % 25) + 1).padStart(2, '0')}`,
  };
});

const planPrices: Record<string, number> = { 'plan-completo': 18500, 'plan-3-dias': 14000, 'plan-musculacion': 12000 };
const methods: Payment['method'][] = ['Efectivo', 'Transferencia', 'Tarjeta'];
const payments: Payment[] = [];
members.forEach((member, index) => {
  if (member.status !== 'Inactivo') {
    payments.push({ id: `pay-jul-${index + 1}`, memberId: member.id, branchId: member.branchId, amount: planPrices[member.planId], paidAt: `2026-07-${String((index % 24) + 1).padStart(2, '0')}T${String(9 + (index % 10)).padStart(2, '0')}:00:00`, period: '2026-07', method: methods[index % methods.length], concept: 'Cuota mensual julio', recordedBy: index % 2 ? 'prof-002' : 'admin-001' });
  }
  if (member.status === 'Activo') {
    payments.push({ id: `pay-ago-${index + 1}`, memberId: member.id, branchId: member.branchId, amount: planPrices[member.planId], paidAt: `2026-08-${String((index % 26) + 1).padStart(2, '0')}T${String(9 + (index % 10)).padStart(2, '0')}:00:00`, period: '2026-08', method: methods[(index + 1) % methods.length], concept: 'Cuota mensual agosto', recordedBy: index % 3 ? 'admin-001' : 'prof-001' });
  }
});

const attendances: Attendance[] = [];
for (let dayOffset = 0; dayOffset < 30; dayOffset += 1) {
  const date = new Date(`${DEMO_TODAY}T12:00:00`);
  date.setDate(date.getDate() - dayOffset);
  const datePart = date.toISOString().slice(0, 10);
  members.forEach((member, index) => {
    if (!(dayOffset === 0 && index === 0) && (member.status === 'Activo' || member.status === 'Moroso') && (index + dayOffset * 2) % 5 < (dayOffset % 7 === 0 ? 2 : 3)) {
      attendances.push({ id: `att-${datePart}-${member.id}`, memberId: member.id, branchId: member.branchId, attendedAt: `${datePart}T${String(8 + (index % 13)).padStart(2, '0')}:${index % 2 ? '30' : '00'}:00` });
    }
  });
}

export const demoSeed: DemoData = {
  version: 3,
  branches: [
    { id: 'palermo', name: 'Palermo', address: 'Av. Santa Fe 3950' },
    { id: 'belgrano', name: 'Belgrano', address: 'Av. Cabildo 2140' },
  ],
  plans: [
    { id: 'plan-completo', name: 'Plan Completo', price: 18500, weeklyAccess: 7 },
    { id: 'plan-3-dias', name: 'Plan 3 Días', price: 14000, weeklyAccess: 3 },
    { id: 'plan-musculacion', name: 'Musculación', price: 12000, weeklyAccess: 5 },
  ],
  professors: [
    { id: 'prof-001', name: 'Alejandro Martínez', email: 'prof.martinez@gymos.ar', branchIds: ['palermo'] },
    { id: 'prof-002', name: 'Carla Rodríguez', email: 'carla.rodriguez@gymos.ar', branchIds: ['belgrano'] },
    { id: 'prof-003', name: 'Marcos García', email: 'marcos.garcia@gymos.ar', branchIds: ['palermo', 'belgrano'] },
  ],
  members,
  payments,
  attendances,
  exercises: [
    { id: 'ex-001', name: 'Sentadilla con Barra', sets: 4, reps: '8', weight: '80 kg', restSec: 90, muscle: 'Cuádriceps' },
    { id: 'ex-002', name: 'Press de Pierna', sets: 3, reps: '12', weight: '120 kg', restSec: 60, muscle: 'Cuádriceps / Glúteo' },
    { id: 'ex-003', name: 'Extensión de Cuádriceps', sets: 3, reps: '15', weight: '45 kg', restSec: 45, muscle: 'Cuádriceps' },
    { id: 'ex-004', name: 'Curl de Isquiotibiales', sets: 4, reps: '12', weight: '35 kg', restSec: 60, muscle: 'Isquiotibiales' },
    { id: 'ex-005', name: 'Peso Muerto Rumano', sets: 3, reps: '10', weight: '60 kg', restSec: 90, muscle: 'Isquiotibiales / Glúteo' },
    { id: 'ex-006', name: 'Elevación de Gemelos', sets: 4, reps: '20', weight: '40 kg', restSec: 30, muscle: 'Gemelos' },
    { id: 'ex-007', name: 'Abducción de Cadera', sets: 3, reps: '15', weight: '30 kg', restSec: 30, muscle: 'Glúteo Medio' },
    { id: 'ex-008', name: 'Press de Banca', sets: 4, reps: '10', weight: '55 kg', restSec: 75, muscle: 'Pecho' },
    { id: 'ex-009', name: 'Remo con Barra', sets: 4, reps: '10', weight: '50 kg', restSec: 75, muscle: 'Espalda' },
  ],
  routines: [
    { id: 'routine-001', name: 'Rutina de Fuerza', dayLabel: 'DÍA 03', focus: 'Piernas y Glúteos', durationMin: 75, intensity: 'Alta Intensidad', exerciseIds: ['ex-001', 'ex-002', 'ex-003', 'ex-004', 'ex-005', 'ex-006', 'ex-007'], objective: 'Aumentar fuerza general', level: 'Avanzado', days: 4 },
    { id: 'routine-002', name: 'Hipertrofia Inicial', dayLabel: 'DÍA 02', focus: 'Pecho y Espalda', durationMin: 60, intensity: 'Media Intensidad', exerciseIds: ['ex-008', 'ex-009', 'ex-003', 'ex-006'], objective: 'Desarrollar masa muscular', level: 'Inicial', days: 3 },
    { id: 'routine-003', name: 'Acondicionamiento General', dayLabel: 'DÍA 01', focus: 'Fuerza general', durationMin: 55, intensity: 'Alta Intensidad', exerciseIds: ['ex-001', 'ex-008', 'ex-009', 'ex-005'], objective: 'Mejorar condición física', level: 'Intermedio', days: 3 },
  ],
  routineProgress: [],
  settings: { gymName: 'GymOS Palermo & Belgrano', currency: 'ARS', timezone: 'America/Argentina/Buenos_Aires', dueReminderDays: 7, allowOverdueAttendance: true },
};

export function cloneDemoSeed(): DemoData {
  return JSON.parse(JSON.stringify(demoSeed)) as DemoData;
}
